/*
 *  -----------------------------------------------------------------------
 *    <copyright file="admin-study-details.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router';
import { useLazyQuery } from '@apollo/client/react';

import StudyCentralMonitoringSetup from '@components/admin/studies/study-cm-setup';
import EditOverviewTables from '@components/admin/studies/study-settings-dialogs/edit-overview-tables';
import ImportAndTRSetupDialog from './import-and-tr-setup-dialog';
import EditSiteSummaryConfiguration from './study-settings-dialogs/edit-site-summary-configuration';
import EditStudySummaryConfDialog from './study-settings-dialogs/edit-study-summary-conf-dialog';
import { LocalStateContext } from 'contexts/local-state-context';
import { useAuthorization, useBoolean, useTranslation } from 'hooks';
import { GET_NAMED_ITEMS_BY_TYPE } from 'queries/common';
import { GET_STUDY_LOGO, GET_STUDY, GET_MANAGE_ORG_STUDIES } from 'queries/studies';
import { GET_ORGANISATION_PROGRAM_PRODUCT } from 'queries/uam';
import { NamedItemType } from 'types/graphql-global-types';
import Constants from 'utils/constants';
import { getIsActiveField } from 'utils/convertor';
import AddStudyDialog from 'views/components/admin/add-study-dialog';
import AssignModulesDialog from 'views/components/admin/assign-modules';
import AddFunctionDialog from 'views/components/admin/studies/function-dialogs/add-function';
import AssignFunctionsListDialog from 'views/components/admin/studies/function-dialogs/assign-functions-list';
import AddIndicationsDialog from 'views/components/admin/studies/indications-dialogs/add-indication-dialog';
import AssignIndicationsListDialog from 'views/components/admin/studies/indications-dialogs/assign-indications-list';
import AddProductDialog from 'views/components/admin/studies/product-dialogs/add-product-dialog';
import AssignProductsListDialog from 'views/components/admin/studies/product-dialogs/assign-products-list';
import StudyContractualInfo from 'views/components/admin/studies/study-contractual-info';
import StudyFunctionsGrid from 'views/components/admin/studies/study-functions-grid';
import StudyIndicationsGrid from 'views/components/admin/studies/study-indications-grid';
import StudyModules from 'views/components/admin/studies/study-modules';
import StudyProductsGrid from 'views/components/admin/studies/study-products-grid';
import StudyRiskAssessmentSetup from 'views/components/admin/studies/study-risk-assessment-setup';
import ContractualInformationDialog from 'views/components/admin/studies/study-settings-dialogs/contractual-information-dialog';
import EditCmDialog from 'views/components/admin/studies/study-settings-dialogs/edit-cm-dialog';
import RiskAssessmentDialog from 'views/components/admin/studies/study-settings-dialogs/risk-assessment-dialog';
import StudySummary from 'views/components/admin/studies/study-summary';
import ItemWrapper from 'views/components/core/item-wrapper';
import Loading from 'views/components/core/loading';
import PageTitle from 'views/components/core/page-title/page-title';
import Error from 'views/components/error/error';
import { AllRoutes } from '@utils/router/all-routes';
import { getNamedItemsByType } from '@queries/generated/common.generated';
import { StudyFields, NamedItemFields } from '@queries/generated/fragments.generated';
import { getStudy, getStudyVariables, getStudyLogo, organisationStudies } from '@queries/generated/studies.generated';
import { getOrganisationProgramProduct } from '@queries/generated/uam.generated';

interface StudyFieldsExtended extends StudyFields {
    _isLogo?: boolean;
    _isRAM?: boolean;
    _isCM?: boolean;
    _isCM2?: boolean;
    _isDQ?: boolean;
}

const AdminStudyDetails = () => {
    const { t } = useTranslation();
    const user = useAuthorization();
    const { state } = useContext(LocalStateContext);
    const navigate = useNavigate();
    const canEdit = !!user?.canWriteSystemAdminLevel?.(false, false, false, true);

    const [loadStudy, { data: studyData, error: errorGetStudy, loading: getStudyLoading }] = useLazyQuery<
        getStudy,
        getStudyVariables
    >(GET_STUDY);
    const [loadPanel, { data: studies }] = useLazyQuery<organisationStudies>(GET_MANAGE_ORG_STUDIES);
    const [loadStudyFunction, { data: allStudyFunctions }] = useLazyQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE);
    const [loadOrganisationProduct, { data: organisationData, loading: orgLoading }] =
        useLazyQuery<getOrganisationProgramProduct>(GET_ORGANISATION_PROGRAM_PRODUCT);

    const openContractual = useBoolean(false);
    const openRiskAssessment = useBoolean(false);
    const openStudySummary = useBoolean(false);
    const openAssignModule = useBoolean(false);
    const openAddFunctionDialog = useBoolean(false);
    const openAssignFunctionDialog = useBoolean(false);
    const openCentralMonitoringSetupDialog = useBoolean(false);
    const openImportAndTRSetupDialog = useBoolean(false);
    const openAddProductDialog = useBoolean(false);
    const openAssignProductDialog = useBoolean(false);
    const fromAssignDialog = useBoolean(false);
    const openAssignIndicatorDialog = useBoolean(false);
    const openAddIndicatorDialog = useBoolean(false);
    const openEditOverviewTables = useBoolean(false);
    const openEditStudySummaryConfDialog = useBoolean(false);
    const openEditSiteSummaryConfiguration = useBoolean(false);
    const programs = organisationData?.organisation?.programs;
    const modules = organisationData?.organisation?.modules;

    const study = studyData?.study;
    const [newStudy, setNewStudy] = useState(study);
    const [studyModules, setStudyModules] = useState<StudyFieldsExtended | null>(study);

    const [loadLogo, { data, error, loading }] = useLazyQuery<getStudyLogo>(GET_STUDY_LOGO);

    useEffect(() => {
        if (study) {
            setStudyModules({
                ...study,
                _isLogo: !!study.logo,
                _isRAM: getIsActiveField('RAM', study.modules),
                _isCM: getIsActiveField('CM', study.modules),
                _isCM2: getIsActiveField('CM2', study.modules),
                _isDQ: getIsActiveField('DQ', study.modules),
                _isSM: getIsActiveField('SM', study.modules),
            });
            loadStudyFunction({ variables: { type: NamedItemType.RESPONSIBLE_FUNCTION } });
            loadOrganisationProduct();
            loadLogo({ variables: { studyId: study?.id as string } });
        }
    }, [study]);

    useEffect(() => {
        if (typeof state?.selectedStudy?.id === 'string') {
            loadStudy({
                variables: {
                    id: state?.selectedStudy?.id,
                },
            });
        }
    }, [state?.selectedStudy?.id]);

    useEffect(() => {
        loadPanel();
    }, [user?.organisationId]);

    const summaryMenu = [
        {
            label: 'uam:edit',
            icon: ['fal', 'edit'],
            onClick: () => {
                if (data?.study)
                    setNewStudy({
                        ...study,
                        logo: data.study.logo,
                    });
                openStudySummary.setTrue();
            },
            enabled: canEdit,
        },
    ];

    const modulesMenu = [
        {
            label: 'admin:assignModules',
            icon: ['fal', 'link'],
            onClick: () => {
                openAssignModule.setTrue();
            },
            enabled: canEdit,
        },
    ];

    const getAvailableFunctions = (): NamedItemFields[] => {
        const associatedItemIds =
            Array.from(
                new Set(
                    allStudyFunctions?.namedItemsByType
                        ?.filter((x) => x?.studyId === study?.id)
                        ?.map((x) => x?.associatedItemId),
                ),
            ) || [];
        return (
            allStudyFunctions?.namedItemsByType?.filter(
                (fun) =>
                    !(study?.studyFunctionIds?.includes(fun?.id) ?? false) &&
                    !(associatedItemIds?.includes(fun?.id) ?? false) &&
                    fun?.studyId === Constants.ObjectId.Empty &&
                    fun?.active,
            ) || []
        );
    };

    const availableFunctions = getAvailableFunctions();

    const chooseFunctionDialog = () => {
        if (availableFunctions.length) openAssignFunctionDialog.setTrue();
        else openAddFunctionDialog.setTrue();
    };
    const getAvailableProducts = () =>
        organisationData?.organisation?.products?.filter((prod) => !study?.productsIds?.includes(prod?.id)) || [];

    const availableProducts = getAvailableProducts();

    const chooseProductDialog = () => {
        if (availableProducts.length) openAssignProductDialog.setTrue();
        else openAddProductDialog.setTrue();
    };

    const getAvailableIndications = () =>
        organisationData?.organisation?.indications?.filter((ind) => !study?.indicationsIds?.includes(ind?.id)) || [];

    const availableIndications = getAvailableIndications();

    const chooseIndicatorDialog = () => {
        if (availableIndications.length) openAssignIndicatorDialog.setTrue();
        else openAddIndicatorDialog.setTrue();
    };

    const contractualInfoMenu = [
        {
            label: 'uam:edit',
            icon: ['fal', 'edit'],
            onClick: () => {
                openContractual.setTrue();
            },
            enabled: canEdit,
        },
    ];

    const riskAssessmentMenu = [
        {
            label: 'uam:edit',
            icon: ['fal', 'edit'],
            onClick: () => {
                openRiskAssessment.setTrue();
            },
            enabled: canEdit,
        },
    ];

    const centralMonitoringMenu = [
        {
            label: 'admin:editCMSetup',
            icon: ['fal', 'edit'],
            onClick: () => openCentralMonitoringSetupDialog.setTrue(),
            enabled: canEdit,
        },
        {
            label: 'admin:editStudyImportAndTRSetup',
            icon: ['fal', 'edit'],
            onClick: () => openImportAndTRSetupDialog.setTrue(),
            enabled: canEdit,
        },
        {
            label: 'admin:editStudySummarySetup',
            icon: ['fal', 'edit'],
            onClick: () => openEditStudySummaryConfDialog.setTrue(),
            enabled: canEdit,
        },
        {
            label: 'admin:siteSummarySetup',
            icon: ['fal', 'edit'],
            onClick: () => openEditSiteSummaryConfiguration.setTrue(),
            enabled: canEdit,
        },
        {
            label: 'admin:editOverviewTables',
            icon: ['fal', 'edit'],
            onClick: () => openEditOverviewTables.setTrue(),
            enabled: canEdit,
        },
    ];

    const functionsMenu = [
        {
            label: 'admin:assignFunctions',
            icon: ['fal', 'link'],
            onClick: () => chooseFunctionDialog(),
            enabled: canEdit,
        },
    ];

    const productsMenu = [
        {
            label: 'admin:assignProducts',
            icon: ['fal', 'link'],
            onClick: () => chooseProductDialog(),
            enabled: canEdit,
        },
    ];

    const indicationsMenu = [
        {
            label: 'admin:assignIndications',
            icon: ['fal', 'link'],
            onClick: () => chooseIndicatorDialog(),
            enabled: canEdit,
        },
    ];

    const onDialogClose = () => {
        openContractual.setFalse();
        openRiskAssessment.setFalse();
        openAssignModule.setFalse();
        openCentralMonitoringSetupDialog.setFalse();
        openImportAndTRSetupDialog.setFalse();
        openEditOverviewTables.setFalse();
        openEditStudySummaryConfDialog.setFalse();
        openEditSiteSummaryConfiguration.setFalse();
        openStudySummary.setFalse();
    };

    const organisation = state?.selectedOrganisation;
    if (organisation && !organisation.studies?.length) return <h3>{t('common:noStudySelected')}</h3>;

    if (!state?.selectedStudy || getStudyLoading || !study) return <Loading float />;

    if (error || errorGetStudy) return <Error error={error || errorGetStudy} />;

    return (
        <>
            <AddIndicationsDialog
                fromAssignDialog={fromAssignDialog.value}
                open={openAddIndicatorDialog.value}
                onClose={(action: string) => {
                    fromAssignDialog.setFalse();
                    openAddIndicatorDialog.setFalse();
                    if (action === 'go-to-indications') navigate(`${AllRoutes.path('admin-org-indications')}`);
                }}
            />
            <AssignIndicationsListDialog
                study={study}
                availableIndications={availableIndications}
                open={openAssignIndicatorDialog.value}
                onClose={(action: string | boolean) => {
                    openAssignIndicatorDialog.setFalse();
                    if (action === 'new-indication') {
                        fromAssignDialog.setTrue();
                        openAddIndicatorDialog.setTrue();
                    }
                }}
            />
            <AddProductDialog
                fromAssignDialog={fromAssignDialog.value}
                open={openAddProductDialog.value}
                onClose={(action: string) => {
                    fromAssignDialog.setFalse();
                    openAddProductDialog.setFalse();
                    if (action === 'go-to-products') navigate(`${AllRoutes.path('admin-org-products')}`);
                }}
            />
            <AssignProductsListDialog
                study={study}
                availableProducts={availableProducts}
                open={openAssignProductDialog.value}
                onClose={(action: string | boolean) => {
                    openAssignProductDialog.setFalse();
                    if (action === 'new-product') {
                        fromAssignDialog.setTrue();
                        openAddProductDialog.setTrue();
                    }
                }}
            />
            <AssignFunctionsListDialog
                study={study}
                availableFunctions={availableFunctions}
                open={openAssignFunctionDialog.value}
                onClose={(action: string | boolean) => {
                    openAssignFunctionDialog.setFalse();
                    if (action === 'new-function') openAddFunctionDialog.setTrue();
                }}
            />
            <AddFunctionDialog
                study={study}
                open={openAddFunctionDialog.value}
                onClose={() => {
                    openAddFunctionDialog.setFalse();
                }}
            />
            <AddStudyDialog
                studySettings
                organisationId={user?.organisationId}
                organisationKey={user?.organisationKey}
                currentStudy={newStudy}
                programs={programs}
                modules={modules}
                open={openStudySummary.value}
                studies={studies?.organisationStudies}
                close={onDialogClose}
                isSysAdmin={user?.isSystemAdmin(Constants.SystemAdminLevel.SysAdmin)}
            />
            <AssignModulesDialog
                studySettings
                organisationId={user?.organisationId}
                modules={modules}
                currentStudy={study}
                close={onDialogClose}
                open={openAssignModule.value}
            />
            <ContractualInformationDialog onClose={onDialogClose} open={openContractual.value} study={study} />

            <RiskAssessmentDialog
                onClose={onDialogClose}
                open={openRiskAssessment.value}
                study={study}
                organisationId={user?.organisationId}
            />

            <EditCmDialog
                open={openCentralMonitoringSetupDialog.value}
                onClose={onDialogClose}
                study={study}
                canEdit={canEdit}
            />

            <ImportAndTRSetupDialog
                open={openImportAndTRSetupDialog.value}
                onClose={onDialogClose}
                study={study}
                canEdit={canEdit}
            />

            {openEditOverviewTables.value && (
                <EditOverviewTables onClose={onDialogClose} study={study} canEdit={canEdit} />
            )}

            <EditSiteSummaryConfiguration
                open={openEditSiteSummaryConfiguration.value}
                onClose={onDialogClose}
                study={study}
                canEdit={canEdit}
            />

            <EditStudySummaryConfDialog
                open={openEditStudySummaryConfDialog.value}
                onClose={onDialogClose}
                study={study}
                canEdit={canEdit}
            />

            <PageTitle text={t('admin:manageStudy')} />

            <ItemWrapper title={t('admin:studySummary')} customRef={null} actions={summaryMenu} hideItemNumber>
                <StudySummary study={study} programs={programs} />
            </ItemWrapper>

            <ItemWrapper title={t('common:modules')} customRef={null} actions={modulesMenu} hideItemNumber>
                <StudyModules study={studyModules} />
            </ItemWrapper>

            <ItemWrapper
                title={t('admin:TRIContractualInformation')}
                customRef={null}
                actions={contractualInfoMenu}
                hideItemNumber
            >
                <StudyContractualInfo study={study} />
            </ItemWrapper>

            <ItemWrapper
                title={t('admin:riskAssessmentSetup')}
                customRef={null}
                actions={riskAssessmentMenu}
                hideItemNumber
            >
                <StudyRiskAssessmentSetup study={study} />
            </ItemWrapper>

            <ItemWrapper
                title={t('admin:centralMonitoringSetup')}
                customRef={null}
                actions={centralMonitoringMenu}
                hideItemNumber
            >
                <StudyCentralMonitoringSetup study={study} localState={state} />
            </ItemWrapper>

            <ItemWrapper title={t('admin:functions')} customRef={null} actions={functionsMenu} hideItemNumber>
                <StudyFunctionsGrid study={study} canEdit={canEdit} />
            </ItemWrapper>

            <ItemWrapper title={t('admin:products')} customRef={null} actions={productsMenu} hideItemNumber>
                <StudyProductsGrid study={study} canEdit={canEdit} />
            </ItemWrapper>

            <ItemWrapper title={t('admin:indications')} customRef={null} actions={indicationsMenu} hideItemNumber>
                <StudyIndicationsGrid study={study} canEdit={canEdit} />
            </ItemWrapper>
        </>
    );
};

export default AdminStudyDetails;
